import {ScreenSnapshot} from './pageContextTypes'

export const SCREEN_TEXT_MAX_CHARS = 80
export const SCREEN_TEXT_MAX_WORDS = 8
export const SCREEN_LIST_MAX_ITEMS = 60
export const SCREEN_SNAPSHOT_MAX_BYTES = 4096

export const SCREEN_LIST_KEYS = ['headings', 'tabs', 'buttons', 'labels', 'columns'] as const
export type ScreenListKey = (typeof SCREEN_LIST_KEYS)[number]

/**
 * One on-screen label, or null when it should not leave the browser.
 *
 * Labels are short. Anything long, wordy, carrying an `@` or five or more digits is far more
 * likely to be data (a name in a heading, an email, a phone or registration number) than
 * chrome, so it is dropped rather than guessed at.
 */
export function cleanScreenText(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const text = raw.replace(/\s+/g, ' ').trim()
  if (!text || text.length > SCREEN_TEXT_MAX_CHARS) return null
  if (text.split(' ').length > SCREEN_TEXT_MAX_WORDS) return null
  if (text.includes('@')) return null
  if ((text.match(/\d/g)?.length ?? 0) >= 5) return null
  return text
}

/** Cleaned, de-duplicated and capped — the same rules wherever a label list is built. */
export function cleanScreenTextList(values: unknown): string[] {
  if (!Array.isArray(values)) return []
  const seen = new Set<string>()
  for (const value of values) {
    const text = cleanScreenText(value)
    if (text) seen.add(text)
    if (seen.size >= SCREEN_LIST_MAX_ITEMS) break
  }
  return [...seen]
}

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length
}

/**
 * The snapshot with every rule applied and empty parts left out, shrunk to the size budget by
 * dropping the last entry of the longest list until it fits.
 */
export function fitScreenSnapshot(raw: unknown): ScreenSnapshot | null {
  const record =
    raw != null && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null
  if (!record) return null

  const lists: Partial<Record<ScreenListKey, string[]>> = {}
  SCREEN_LIST_KEYS.forEach((key) => {
    const list = cleanScreenTextList(record[key])
    if (list.length > 0) lists[key] = list
  })
  const title = cleanScreenText(record.title)

  const build = (): ScreenSnapshot => ({...(title ? {title} : {}), ...lists})

  let snapshot = build()
  while (byteLength(snapshot) > SCREEN_SNAPSHOT_MAX_BYTES) {
    const longest = SCREEN_LIST_KEYS.filter((key) => lists[key]?.length).sort(
      (a, b) => (lists[b]?.length ?? 0) - (lists[a]?.length ?? 0)
    )[0]
    if (!longest) break
    lists[longest] = lists[longest]!.slice(0, -1)
    if (lists[longest]!.length === 0) delete lists[longest]
    snapshot = build()
  }

  return Object.keys(snapshot).length > 0 ? snapshot : null
}
